/**
 * useBreakpoint — Tailwind-aligned matchMedia hooks.
 *
 * Centralizes breakpoint queries so consumers don't sprinkle hardcoded
 * `matchMedia('(max-width: 767px)')` calls throughout the codebase. The
 * `js-breakpoint-literals` governance validator excludes `/hooks/useBreakpoint`
 * paths so this file can house the literals once.
 *
 * Tailwind v4 default breakpoints (mirrored from `@plerity/tokens-apps/tailwind-preset`):
 *   sm  640px
 *   md  768px
 *   lg  1024px
 *   xl  1280px
 *   2xl 1536px
 *
 * Below-md = "mobile" in Plerity's dashboard convention. Below-lg = the
 * threshold where `createShell` auto-collapses the sidebar (also where
 * the MobileMenuButton appears via `lg:hidden`).
 *
 * SSR-safe: returns the `defaultValue` (default `false`) on the server where
 * `window` is undefined, then re-hydrates on first client render.
 *
 * @package @plerity/dashboard-shared
 */

import { useEffect, useState } from "react";

/**
 * Tailwind breakpoint names. Matches the canonical preset.
 * Note: `xs` is below-sm (no Tailwind utility) — represented as the
 * `useIsMobile()` convenience below.
 */
export type Breakpoint = "sm" | "md" | "lg" | "xl" | "2xl";

/**
 * px values for each breakpoint — matches @plerity/tokens-apps/tailwind-preset.
 * Exported (not just internal) so one-shot, non-reactive `window.innerWidth`/
 * `matchMedia` reads outside a component's render body (event handlers,
 * imperative "read once per mount" checks, lazy `useState` initializers —
 * none of which can call the `useIsBelow`/`useIsAtLeast` hooks below) can
 * still cite this single source of truth instead of a bare magic number.
 */
export const BREAKPOINT_PX: Record<Breakpoint, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

/**
 * Subscribe to a `matchMedia` query and return its current `matches` state.
 * Re-renders the consumer when the match flips.
 *
 * @param query CSS media query string (e.g. `(max-width: 767px)`).
 * @param defaultValue Value to return when `window` is undefined (SSR).
 *                     Default false; pass true for queries that "feel" true
 *                     on the server (e.g. dark-mode preference).
 */
export function useMediaQuery(query: string, defaultValue = false): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined") return defaultValue;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const update = (e: MediaQueryListEvent | MediaQueryList) => setMatches(e.matches);
    update(mq);
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [query]);

  return matches;
}

/**
 * `true` when the viewport is BELOW the named breakpoint.
 *
 * `useIsBelow('md')` returns true at viewport widths < 768 px (i.e.
 * Tailwind's `max-md:` selector range).
 */
export function useIsBelow(breakpoint: Breakpoint): boolean {
  // Subtract 1 so the boundary is exclusive — `md` itself (768) is NOT mobile.
  const px = BREAKPOINT_PX[breakpoint] - 1;
  return useMediaQuery(`(max-width: ${px}px)`);
}

/**
 * `true` when the viewport is AT OR ABOVE the named breakpoint.
 *
 * `useIsAtLeast('lg')` returns true at viewport widths >= 1024 px (i.e.
 * Tailwind's `lg:` selector range).
 */
export function useIsAtLeast(breakpoint: Breakpoint): boolean {
  const px = BREAKPOINT_PX[breakpoint];
  return useMediaQuery(`(min-width: ${px}px)`);
}

/**
 * Convenience: `true` when viewport is below the `md` breakpoint (768 px).
 * Plerity's "mobile" convention — the threshold below which the dashboard
 * sidebar becomes an off-canvas drawer.
 */
export function useIsMobile(): boolean {
  return useIsBelow("md");
}
